import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { SurveyTableInterface } from '../../shared/interfaces/survey-table.interface';
import {
  Quotas,
  Survey,
  TcResponse,
  Criteria,
} from '../../shared/interfaces/survey.interface';
import { SurveyStatus } from '../../shared/utils/survey-status';
import { Observable, Subject, Subscription } from 'rxjs';
import { Constants as TC_CONSTANTS } from '../constants/manage-traffic-channels.constants';
import { BuyerApiService } from '../../shared/services/buyer-api/survey.service';
import { SocketService } from '../../shared/services/socket/socket.service';
import { ModalService } from '@purespectrum1/ui/modal';
import { EventEmitterService } from '../../shared/services/event-emitter/event-emitter.service';
import { TrafficChannelModalComponent } from './traffic-channel-modal/traffic-channel-modal.component';
import { Constants as QUOTA_CONSTANTS } from '../constants/manage-quotas.constants';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { notifyMessage } from '../../shared/constants/notify-message';
import { startWith } from 'rxjs/operators';

@Component({
  selector: 'ps-ui-manage-traffic-channels',
  templateUrl: './manage-traffic-channels.component.html',
  styleUrls: ['./manage-traffic-channels.component.css'],
})
export class ManageTrafficChannelsComponent implements OnInit, OnDestroy {
  private _subscriptions: Subscription = new Subscription();
  @Input() survey: Survey = {} as Survey;
  @Input() isCpiEditModeDisabled: boolean = false;
  surveyQuotas: Array<Quotas> = [];
  surveyStatus = SurveyStatus;
  tcSurveys: Array<TcResponse> = [];
  tcSurveyTableHeaders = TC_CONSTANTS.TC_SURVEY_TABLE_HEADERS;
  surveyTableData: Array<SurveyTableInterface> = [];

  // To make this component work as a embedded widget we need to control the render
  // The new table don't know when changes occurs to render this component
  // We should refactor this component but this is out of PD-7365 scope
  // Todo: Refactor this component to OnPush strategy
  private readonly _renderCommand = new Subject<SurveyTableInterface[]>();
  public surveys$!: Observable<SurveyTableInterface[]>;

  constructor(
    private _buyerApiService: BuyerApiService,
    private _socketService: SocketService,
    private _modal: ModalService,
    private _eventEmitterService: EventEmitterService,
    private _toastr: ToasterService
  ) {}

  ngOnInit(): void {
    this.surveys$ = this._renderCommand.pipe(startWith([]));

    this._getQuotaDetails();
    this._getTrafficChannelSurveys();
    this._socketSubscriptions();
    this._subscriptions.add(
      this._eventEmitterService.getTcConfirmationEmitter.subscribe((msg) => {
        if (msg == 'created') {
          this._getTrafficChannelSurveys();
          this._getDataForTable();
        }
      })
    );
  }
  private _getQuotaDetails() {
    if (this.survey.qbp?.quota_ids?.length) {
      const surveyId = this.survey.id;
      const params = {
        page: 1,
        limit: QUOTA_CONSTANTS.QUOTAPAGELIMIT,
        search: '',
      };
      this._buyerApiService
        .getQuotaDetails(surveyId, params)
        .subscribe((response) => {
          if (response?.quota) {
            this.surveyQuotas = response.quota;
          }
        });
    }
  }

  private _socketSubscriptions() {
    this._subscriptions.add(
      this._socketService.trafficChannelsTab$.subscribe((surveyId) => {
        if (Number(this.survey.id) === surveyId) {
          this._getTrafficChannelSurveys(surveyId);
        }
      })
    );
    this._subscriptions.add(
      this._socketService.surveyStatusChange$.subscribe((data) => {
        this._updateSurveyStatus(data);
      })
    );
  }

  private _getTrafficChannelSurveys(surveyId: number = this.survey.id) {
    this._buyerApiService
      .getTrafficChannelData(surveyId)
      .subscribe((response) => {
        this.tcSurveys = response;
        this._getDataForTable();
      });
  }

  private _updateSurveyStatus(data: {
    oldStatus: number;
    newStatus: number;
    surveyId: number;
  }) {
    if (Number(this.survey.id) === data.surveyId) {
      this.survey.status = data.newStatus;
    }

    const index = this.tcSurveys.findIndex(
      (tc) => tc.survey_id == data.surveyId
    );
    if (index !== -1) {
      this.tcSurveys[index].status = data.newStatus;
    }
  }

  createTCSurvey(
    type: string,
    requestFrom: string,
    surveyDetails?: Survey,
    tcSurveys?: TcResponse[],
    surveyQuotas?: Array<Quotas>
  ) {
    let TCModalData = {
      type: type,
      survey: surveyDetails || this.survey,
      surveyQuotas: surveyQuotas || this.surveyQuotas,
      tcSurveys: tcSurveys || this.tcSurveys,
    };

    const FirstCapType = type
      .toLowerCase()
      .replace(/^./, type.toLowerCase()[0].toUpperCase());

    const modalRef = this._modal.open(TrafficChannelModalComponent, {
      data: TCModalData,
      width: '60%',
    });
    modalRef.onClose$.subscribe((msg) => {
      if (msg === 'Created' && requestFrom === 'surveyDetailsPage') {
        this._getDataForTable();
        this._getTrafficChannelSurveys();
      } else if (
        msg === 'Created' &&
        requestFrom === 'surveyListingPage' &&
        type !== 'SPECIFIC AUDIENCE'
      ) {
        this._toastr.success(
          `${FirstCapType} ${notifyMessage.TRAFFIC_CHANNEL_SURVEY_CREATED_SUBPART} ${surveyDetails?.id}`
        );
      }
    });
  }

  getQuotaName(id: string) {
    const quotaIndex = this.surveyQuotas.findIndex(
      (quota) => quota.ps_quota_id === id
    );
    return quotaIndex > -1
      ? `${
          this.surveyQuotas[quotaIndex].group_key
        } | ${this._getNestedQuotaGroup(
          this.surveyQuotas[quotaIndex].criteria
        )}`
      : '';
  }

  private _getNestedQuotaGroup(quotaCriteria: Criteria[]) {
    let group_data = '';
    quotaCriteria.forEach((item: Criteria) => {
      if (item.qualification_name === 'Age') {
        const rangeItem = item.range_sets[0];
        group_data = group_data
          ? `${group_data}, ${rangeItem.from} yr - ${rangeItem.to} yr`
          : `${rangeItem.from} yr - ${rangeItem.to} yr`;
      } else if (item.qualification_name === 'Household Income') {
        const rangeItem = item.range_sets[0];
        group_data = group_data
          ? `${group_data}, ${rangeItem.from} - ${rangeItem.to}`
          : `${rangeItem.from} - ${rangeItem.to}`;
      } else {
        group_data = group_data
          ? group_data + ', ' + item.condition_names[0]
          : item.condition_names[0];
      }
    });
    return group_data;
  }

  private _getDataForTable() {
    this.surveyTableData = [];
    this.surveyTableData.push(this._mapSurveyDataForTable());
    let tcMapTcSurveys = this.tcSurveys
      .filter((tcSurvey) => tcSurvey.status !== SurveyStatus.Draft)
      .map((tcSurvey) => {
        return {
          ps_survey_status: tcSurvey.status,
          ps_survey_id: tcSurvey.survey_id,
          survey_title: tcSurvey.title,
          survey_type: this._getSurveyType(
            tcSurvey.channel_type,
            tcSurvey.qbpQuota.id
          ),
          creation_date: tcSurvey.launch_date,
          fielded: tcSurvey.fielded,
          total_cost: { current: 0 },
          goal: tcSurvey.goal,
          currencySymbol: tcSurvey.symbol,
          avg_cpi: tcSurvey.avg_cpi,
          cpi_current: tcSurvey.cpi_current,
          epc: tcSurvey.epc,
          current_ir: tcSurvey.current_ir,
          total_starts: tcSurvey.total_starts,
          last_24_starts: tcSurvey.last_24_starts,
          in_progress: tcSurvey.in_progress,
          create_status: tcSurvey.create_status,
          description: tcSurvey.description,
          tc_status_id: tcSurvey.child_status_id,
          create_status_description: tcSurvey.create_status_description,
        };
      });
    tcMapTcSurveys.sort(function (a: any, b: any) {
      return a.ps_survey_id - b.ps_survey_id;
    });
    this.surveyTableData.push(...tcMapTcSurveys);
    this._renderCommand.next(this.surveyTableData);
  }

  private _mapSurveyDataForTable() {
    return {
      ps_survey_status: this.survey.status,
      ps_survey_id: this.survey.id,
      survey_title: this.survey.title,
      survey_type: 'Parent',
      creation_date: this.survey.launch_date || 0,
      fielded: this.survey.fielded,
      goal: this.survey.goal,
      currencySymbol: this.survey.currencySymbol,
      avg_cpi: this.survey.fielded
        ? this.survey.totalCost.current / this.survey.fielded
        : 0,
      cpi_current: this.survey.cpi.current
        ? Number(this.survey.cpi.current)
        : 0,
      current_ir:
        this.survey.incidence.overall === undefined
          ? '-'
          : this.survey.incidence.overall,
      epc: this.survey.epc || 0,
      incidence: this.survey.incidence,
      total_starts: this.survey.total_starts || 0,
      last_24_starts: this.survey.last_24_starts || 0,
      in_progress: this.survey.in_progress || 0,
      invoiceType: this.survey.invoiceType,
    };
  }

  private _getSurveyType(type: string, id: string) {
    const surveyTypeObj: ObjIndexable = {
      REP: 'REPLICA',
      UNT: 'UNTARGETED',
      SPA: 'SPECIFIC AUDIENCE',
      TL: 'TARGETED LIST',
      LZ: 'Large Zip',
      AP: 'Allocation Priority',
      SR: 'Stealth Replica',
    };

    return `${surveyTypeObj[type]} ${this.getQuotaName(id)}`;
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }
}
interface ObjIndexable {
  [key: string]: any;
}
